export type VideoDiagnostic = {
  timestamp: string;
  eventName: string;
  currentTime: number;
  duration: number | null;
  paused: boolean;
  ended: boolean;
  readyState: number;
  networkState: number;
  errorCode: number | null;
  visibilityState: string;
  recoveryAttempt: number;
  watchdogStatus: string;
};

type Timer = ReturnType<typeof setTimeout>;

export type ResilientVideoControllerOptions = {
  video: HTMLVideoElement;
  isActive: () => boolean;
  getSource: () => string | null;
  debug?: boolean;
  onDiagnostic?: (diagnostic: VideoDiagnostic) => void;
  backoffMs?: number[];
  watchdogMs?: number;
  bufferingGraceMs?: number;
  stablePlaybackMs?: number;
  playTimeoutMs?: number;
  recoveryLockTimeoutMs?: number;
};

const MEDIA_EVENTS = ["ended", "pause", "stalled", "waiting", "suspend", "abort", "emptied", "error", "canplay", "playing"] as const;
const DEFAULT_BACKOFF = [1000, 2000, 5000, 10000, 30000];
const HAVE_CURRENT_DATA = 2;
const HAVE_FUTURE_DATA = 3;
const NETWORK_NO_SOURCE = 3;

function finiteTime(value: number) {
  return Number.isFinite(value) && value >= 0 ? value : 0;
}

function cacheBusted(source: string) {
  const separator = source.includes("?") ? "&" : "?";
  return `${source}${separator}recovery=${Date.now()}`;
}

export class ResilientVideoController {
  private readonly video: HTMLVideoElement;
  private readonly options: ResilientVideoControllerOptions;
  private watchdogTimer: Timer | null = null;
  private bufferingTimer: Timer | null = null;
  private retryTimer: Timer | null = null;
  private stableTimer: Timer | null = null;
  private recoveryInProgress = false;
  private recoveryStartedAt = 0;
  private recoveryGeneration = 0;
  private pendingRecoveryReason: string | null = null;
  private nextRecoveryAt = 0;
  private recoveryAttempt = 0;
  private previousTime = -1;
  private frozenChecks = 0;
  private playingSince = 0;
  private playingStartTime = 0;
  private manualPause = false;
  private stopped = false;
  private lastEvent = "initialised";
  private watchdogStatus = "idle";
  private readonly mediaHandler = (event: Event) => this.onMediaEvent(event.type);
  private readonly visibilityHandler = () => {
    if (document.visibilityState === "visible") void this.resume("visibilitychange");
    else this.log("visibilitychange");
  };
  private readonly pageShowHandler = () => void this.resume("pageshow");
  private readonly focusHandler = () => void this.resume("focus");
  private readonly onlineHandler = () => void this.resume("online");

  constructor(options: ResilientVideoControllerOptions) {
    this.options = options;
    this.video = options.video;
  }

  start() {
    this.stopped = false;
    MEDIA_EVENTS.forEach((name) => this.video.addEventListener(name, this.mediaHandler));
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", this.visibilityHandler);
    if (typeof window !== "undefined") {
      window.addEventListener("pageshow", this.pageShowHandler);
      window.addEventListener("focus", this.focusHandler);
      window.addEventListener("online", this.onlineHandler);
    }
    this.scheduleWatchdog();
  }

  stop() {
    this.stopped = true;
    MEDIA_EVENTS.forEach((name) => this.video.removeEventListener(name, this.mediaHandler));
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", this.visibilityHandler);
    if (typeof window !== "undefined") {
      window.removeEventListener("pageshow", this.pageShowHandler);
      window.removeEventListener("focus", this.focusHandler);
      window.removeEventListener("online", this.onlineHandler);
    }
    this.clearTimers();
    this.invalidateRecovery();
  }

  sourceChanged() {
    this.cancelRecoveryTimers();
    this.invalidateRecovery();
    this.previousTime = -1;
    this.frozenChecks = 0;
    this.recoveryAttempt = 0;
    this.playingSince = 0;
    this.log("source-changed");
    if (this.options.isActive()) void this.resume("source-changed");
  }

  setActive(active: boolean) {
    if (active) {
      this.watchdogStatus = "starting";
      this.previousTime = -1;
      this.frozenChecks = 0;
      void this.resume("screensaver-active");
      return;
    }
    this.watchdogStatus = "inactive";
    this.cancelRecoveryTimers();
    this.invalidateRecovery();
    this.manualPause = true;
    this.video.pause();
    if (finiteTime(this.video.duration) > 0) {
      try { this.video.currentTime = 0; } catch { /* Some embedded browsers reject seeks before metadata. */ }
    }
    this.manualPause = false;
  }

  async resume(reason: string) {
    if (!this.options.isActive() || this.stopped || this.recoveryInProgress) return;
    this.applyMutedState();
    this.log(reason);
    try {
      await this.playWithTimeout();
    } catch (error) {
      this.log(`${reason}-play-rejected`, error);
      this.scheduleRecovery(`${reason}-play-rejected`);
    }
  }

  runWatchdog() {
    if (!this.options.isActive() || this.stopped) {
      this.watchdogStatus = "inactive";
      return;
    }

    this.expireStaleRecoveryLock();
    this.runScheduledRecovery();

    const video = this.video;
    const time = finiteTime(video.currentTime);
    const wrapped = this.previousTime >= 0 && time + 0.25 < this.previousTime && !video.ended;
    const advanced = this.previousTime < 0 || time > this.previousTime + 0.05 || wrapped;
    const unavailable = video.readyState < HAVE_CURRENT_DATA || video.networkState === NETWORK_NO_SOURCE;

    if (video.paused || video.ended || unavailable) {
      this.watchdogStatus = video.ended ? "ended" : video.paused ? "paused" : "unavailable";
      this.log(`watchdog-${this.watchdogStatus}`);
      this.scheduleRecovery(`watchdog-${this.watchdogStatus}`);
    } else if (!advanced) {
      this.frozenChecks += 1;
      this.watchdogStatus = `possible-freeze-${this.frozenChecks}`;
      this.log("watchdog-no-progress");
      if (this.frozenChecks >= 2) this.scheduleRecovery("watchdog-frozen");
    } else {
      this.frozenChecks = 0;
      this.watchdogStatus = "playing";
      if (this.playingSince && Date.now() - this.playingSince >= (this.options.stablePlaybackMs ?? 10_000)) {
        this.resetRetries("stable-playback");
      } else if (this.options.debug) {
        this.log("watchdog-playing");
      }
    }
    this.previousTime = time;
  }

  isRecovering() {
    return this.recoveryInProgress;
  }

  getRecoveryAttempt() {
    return this.recoveryAttempt;
  }

  async recoverNow(reason: string) {
    await this.recover(reason);
  }

  private onMediaEvent(name: string) {
    this.lastEvent = name;
    this.log(name);
    if (!this.options.isActive()) return;

    if (name === "ended") {
      try { this.video.currentTime = 0; } catch { /* ignored */ }
      void this.resume("ended-restart");
      return;
    }
    if (name === "playing") {
      this.clearBufferingTimer();
      this.frozenChecks = 0;
      this.previousTime = finiteTime(this.video.currentTime);
      this.playingSince = Date.now();
      this.playingStartTime = this.previousTime;
      this.scheduleStableReset();
      return;
    }
    if (name === "canplay") {
      void this.resume("canplay");
      return;
    }
    if (name === "waiting" || name === "stalled" || name === "suspend") {
      this.scheduleBufferingCheck(name);
      return;
    }
    if (name === "pause" && !this.manualPause) {
      this.scheduleRecovery("pause");
      return;
    }
    if (name === "abort" || name === "emptied" || name === "error") {
      this.scheduleRecovery(name);
    }
  }

  private scheduleWatchdog() {
    if (this.watchdogTimer) clearTimeout(this.watchdogTimer);
    this.watchdogTimer = setTimeout(() => {
      this.watchdogTimer = null;
      this.runWatchdog();
      if (!this.stopped) this.scheduleWatchdog();
    }, this.options.watchdogMs ?? 3000);
  }

  private scheduleBufferingCheck(eventName: string) {
    if (this.bufferingTimer) return;
    this.watchdogStatus = `buffering-${eventName}`;
    this.bufferingTimer = setTimeout(() => {
      this.bufferingTimer = null;
      if (!this.options.isActive() || this.stopped) return;
      const hasNotResumed = this.video.paused || this.video.readyState < HAVE_FUTURE_DATA;
      if (hasNotResumed) this.scheduleRecovery(`${eventName}-timeout`);
    }, this.options.bufferingGraceMs ?? 5000);
  }

  private scheduleRecovery(reason: string) {
    if (!this.options.isActive() || this.stopped || this.recoveryInProgress) return;
    if (this.pendingRecoveryReason) {
      this.runScheduledRecovery();
      return;
    }
    const backoff = this.options.backoffMs ?? DEFAULT_BACKOFF;
    const delay = backoff[Math.min(this.recoveryAttempt, backoff.length - 1)] ?? 30_000;
    this.pendingRecoveryReason = reason;
    this.nextRecoveryAt = Date.now() + delay;
    this.watchdogStatus = `retry-in-${delay}`;
    this.log(`recovery-scheduled:${reason}`);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.runScheduledRecovery();
    }, delay);
  }

  private runScheduledRecovery() {
    if (!this.pendingRecoveryReason || this.recoveryInProgress || this.stopped || !this.options.isActive()) return;
    if (Date.now() < this.nextRecoveryAt) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    const reason = this.pendingRecoveryReason;
    this.pendingRecoveryReason = null;
    this.nextRecoveryAt = 0;
    void this.recover(reason);
  }

  private expireStaleRecoveryLock() {
    const maxLockMs = this.options.recoveryLockTimeoutMs ?? 15_000;
    if (!this.recoveryInProgress || !this.recoveryStartedAt || Date.now() - this.recoveryStartedAt < maxLockMs) return;
    this.invalidateRecovery();
    this.watchdogStatus = "recovery-lock-expired";
    this.log("recovery-lock-timeout");
    this.scheduleRecovery("recovery-lock-timeout");
  }

  private async recover(reason: string) {
    if (!this.options.isActive() || this.stopped || this.recoveryInProgress) return;
    this.recoveryInProgress = true;
    this.recoveryStartedAt = Date.now();
    const generation = ++this.recoveryGeneration;
    this.recoveryAttempt += 1;
    const level = Math.min(this.recoveryAttempt, 3);
    this.watchdogStatus = `recovery-level-${level}`;
    this.log(`recovery-${level}-start:${reason}`);

    try {
      this.applyMutedState();
      if (level === 1) {
        await this.playWithTimeout();
      } else if (level === 2) {
        const position = finiteTime(this.video.currentTime);
        this.video.load();
        await this.waitForCanPlay();
        if (position > 0 && Number.isFinite(this.video.duration) && position < this.video.duration) {
          try { this.video.currentTime = position; } catch { /* seek is best effort */ }
        }
        await this.playWithTimeout();
      } else {
        const source = this.options.getSource() || this.video.currentSrc || this.video.src;
        if (!source) throw new Error("No video source is available");
        this.video.src = cacheBusted(source);
        this.video.load();
        await this.waitForCanPlay();
        await this.playWithTimeout();
      }
      this.log(`recovery-${level}-play-requested`);
    } catch (error) {
      if (generation !== this.recoveryGeneration) return;
      this.log(`recovery-${level}-failed`, error);
      this.recoveryInProgress = false;
      this.recoveryStartedAt = 0;
      this.scheduleRecovery(`level-${level}-failed`);
      return;
    }
    if (generation !== this.recoveryGeneration) return;
    this.recoveryInProgress = false;
    this.recoveryStartedAt = 0;
  }

  private playWithTimeout() {
    const timeoutMs = this.options.playTimeoutMs ?? 6000;
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const finish = (error?: unknown) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        error ? reject(error) : resolve();
      };
      const timeout = setTimeout(() => finish(new Error(`video.play() did not settle within ${timeoutMs}ms`)), timeoutMs);
      try {
        Promise.resolve(this.video.play()).then(() => finish(), (error) => finish(error));
      } catch (error) { finish(error); }
    });
  }

  private invalidateRecovery() {
    this.recoveryGeneration += 1;
    this.recoveryInProgress = false;
    this.recoveryStartedAt = 0;
  }

  private waitForCanPlay() {
    if (this.video.readyState >= HAVE_FUTURE_DATA) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => finish(new Error("Timed out waiting for canplay")), 8000);
      const onCanPlay = () => finish();
      const onError = () => finish(new Error("Video failed while loading"));
      const finish = (error?: Error) => {
        clearTimeout(timeout);
        this.video.removeEventListener("canplay", onCanPlay);
        this.video.removeEventListener("error", onError);
        error ? reject(error) : resolve();
      };
      this.video.addEventListener("canplay", onCanPlay, { once: true });
      this.video.addEventListener("error", onError, { once: true });
    });
  }

  private applyMutedState() {
    this.video.muted = true;
    this.video.defaultMuted = true;
  }

  private scheduleStableReset() {
    if (this.stableTimer) clearTimeout(this.stableTimer);
    const delay = this.options.stablePlaybackMs ?? 10_000;
    this.stableTimer = setTimeout(() => {
      this.stableTimer = null;
      const progressed = finiteTime(this.video.currentTime) > this.playingStartTime + 0.25;
      if (this.options.isActive() && !this.video.paused && progressed) this.resetRetries("stable-playback");
    }, delay);
  }

  private resetRetries(reason: string) {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.pendingRecoveryReason = null;
    this.nextRecoveryAt = 0;
    if (this.recoveryAttempt === 0) return;
    this.recoveryAttempt = 0;
    this.log(reason);
  }

  private cancelRecoveryTimers() {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.bufferingTimer) clearTimeout(this.bufferingTimer);
    if (this.stableTimer) clearTimeout(this.stableTimer);
    this.retryTimer = null;
    this.pendingRecoveryReason = null;
    this.nextRecoveryAt = 0;
    this.bufferingTimer = null;
    this.stableTimer = null;
  }

  private clearBufferingTimer() {
    if (this.bufferingTimer) clearTimeout(this.bufferingTimer);
    this.bufferingTimer = null;
  }

  private clearTimers() {
    this.cancelRecoveryTimers();
    if (this.watchdogTimer) clearTimeout(this.watchdogTimer);
    this.watchdogTimer = null;
  }

  private log(eventName: string, _error?: unknown) {
    this.lastEvent = eventName;
    const diagnostic: VideoDiagnostic = {
      timestamp: new Date().toISOString(),
      eventName,
      currentTime: finiteTime(this.video.currentTime),
      duration: Number.isFinite(this.video.duration) ? this.video.duration : null,
      paused: this.video.paused,
      ended: this.video.ended,
      readyState: this.video.readyState,
      networkState: this.video.networkState,
      errorCode: this.video.error?.code ?? null,
      visibilityState: typeof document === "undefined" ? "unknown" : document.visibilityState,
      recoveryAttempt: this.recoveryAttempt,
      watchdogStatus: this.watchdogStatus
    };
    this.options.onDiagnostic?.(diagnostic);
  }
}
